(() => {
  'use strict';

  const config = window.distopionCartThresholdConfig;
  if (!config || !window.Shopify?.routes?.root) return;

  const shopCurrency = String(config.shopCurrency || '').toUpperCase();
  const baseThresholds = Array.isArray(config.thresholds)
    ? config.thresholds
        .map((threshold) => ({
          amount: Number(threshold?.amount || 0),
          label: String(threshold?.label || ''),
        }))
        .filter((threshold) => Number.isFinite(threshold.amount) && threshold.amount > 0)
    : [];

  if (!shopCurrency || baseThresholds.length === 0) return;

  const root = window.Shopify.routes.root;
  const messages = config.messages || {};

  let latestCart = null;
  let frame = 0;
  let renderTimer = 0;
  let exactReadTimer = 0;
  let cartRequest = null;

  const isExactCart = (cart) =>
    Array.isArray(cart?.items) && typeof cart?.currency === 'string';

  const escapeHtml = (value) =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const currencyRate = (cartCurrency) => {
    if (cartCurrency === shopCurrency) return 1;

    const activeCurrency = String(window.Shopify?.currency?.active || '').toUpperCase();
    const rate = Number(window.Shopify?.currency?.rate);

    if (
      cartCurrency &&
      activeCurrency === cartCurrency &&
      Number.isFinite(rate) &&
      rate > 0
    ) {
      return rate;
    }

    return null;
  };

  const formatMoney = (minorUnits, currency) => {
    const amount = Math.max(0, Number(minorUnits || 0)) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format(amount);
    } catch (_error) {
      return `${amount.toFixed(2)} ${currency}`;
    }
  };

  const renderTemplate = (template, replacements = {}) => {
    let output = String(template || '');

    Object.entries(replacements).forEach(([token, value]) => {
      output = output.replaceAll(token, escapeHtml(value));
    });

    return output;
  };

  const messageElement = (bar) =>
    bar.querySelector('.free-shipping-bar__message') ||
    bar.querySelector(':scope > span');

  const setMessage = (bar, template, replacements) => {
    const target = messageElement(bar);
    if (!target || !template) return;
    target.innerHTML = renderTemplate(template, replacements);
  };

  const replaceProgress = (progress, now, max) => {
    if (!progress) return null;

    const nextNow = Math.max(0, Math.min(Math.round(now), Math.round(max)));
    const nextMax = Math.max(1, Math.round(max));

    if (
      progress.getAttribute('aria-valuenow') === String(nextNow) &&
      progress.getAttribute('aria-valuemax') === String(nextMax)
    ) {
      return progress;
    }

    const replacement = progress.cloneNode(true);
    replacement.setAttribute('aria-valuenow', String(nextNow));
    replacement.setAttribute('aria-valuemax', String(nextMax));
    progress.replaceWith(replacement);

    return replacement;
  };

  const cartShippingSubtotal = (cart) => {
    if (!isExactCart(cart)) return null;

    const shippingItems = cart.items.reduce((total, item) => {
      if (!item?.requires_shipping) return total;
      return total + Number(item.final_line_price || 0);
    }, 0);

    const cartDiscount = (cart.cart_level_discount_applications || []).reduce(
      (total, discount) => total + Number(discount.total_allocated_amount || 0),
      0
    );

    return Math.max(0, shippingItems - cartDiscount);
  };

  const domCurrentTotal = (bar) => {
    if (bar.classList.contains('free-shipping-bar--tiers')) {
      return Array.from(
        bar.querySelectorAll('.free-shipping-bar__tier-fill[aria-valuenow]')
      ).reduce(
        (total, progress) => total + Number(progress.getAttribute('aria-valuenow') || 0),
        0
      );
    }

    const progress = bar.querySelector(
      '#progress-bar-free-shipping-threshold, .progress-bar[aria-valuenow]'
    );

    return Number(progress?.getAttribute('aria-valuenow') || 0);
  };

  const convertedThresholds = (rate) =>
    baseThresholds.map((threshold) => ({
      amount: Math.ceil(threshold.amount * rate),
      label: threshold.label,
    }));

  const nextThreshold = (thresholds, currentTotal) =>
    thresholds.reduce((next, threshold) => {
      if (currentTotal >= threshold.amount) return next;
      if (!next || threshold.amount < next.amount) return threshold;
      return next;
    }, null);

  const syncAmountLeftControl = (bar) => {
    bar.closest('cart-drawer')
      ?.querySelector('[data-distopion-amount-left]')
      ?.remove();
  };

  const updateClassic = (bar, thresholds, currentTotal, currency) => {
    const threshold = thresholds[0];
    if (!threshold) return;

    const reached = currentTotal >= threshold.amount;
    const remaining = Math.max(0, threshold.amount - currentTotal);

    bar.classList.toggle('is-complete', reached);

    if (reached) {
      setMessage(bar, messages.classicReached);
    } else {
      setMessage(bar, messages.classicUnreached, {
        __AMOUNT__: formatMoney(remaining, currency),
      });
    }

    replaceProgress(
      bar.querySelector(
        '#progress-bar-free-shipping-threshold, .progress-bar[aria-valuenow]'
      ),
      currentTotal,
      threshold.amount
    );
  };

  const updateMilestones = (bar, thresholds, currentTotal, currency) => {
    const maxThreshold = Math.max(...thresholds.map((threshold) => threshold.amount), 1);
    const next = nextThreshold(thresholds, currentTotal);

    bar.classList.toggle('is-complete', !next);

    if (next) {
      setMessage(bar, messages.multiUnreached, {
        __AMOUNT__: formatMoney(next.amount - currentTotal, currency),
        __GOAL__: next.label,
      });
    } else {
      setMessage(bar, messages.allReached);
    }

    replaceProgress(
      bar.querySelector('#progress-bar-free-shipping-threshold'),
      currentTotal,
      maxThreshold
    );

    const milestones = bar.querySelectorAll('.free-shipping-bar__milestone');

    milestones.forEach((milestone, index) => {
      const threshold = thresholds[index];
      if (!threshold) return;

      const position = (threshold.amount / maxThreshold) * 100;
      milestone.style.insetInlineStart = `${position}%`;
      milestone.classList.toggle('is-reached', currentTotal >= threshold.amount);
      milestone.classList.toggle(
        'free-shipping-bar__milestone--edge-end',
        position > 85
      );
    });
  };

  const updateTiers = (bar, thresholds, currentTotal, currency) => {
    const maxThreshold = Math.max(...thresholds.map((threshold) => threshold.amount), 1);
    const next = nextThreshold(thresholds, currentTotal);

    bar.classList.toggle('is-complete', !next);

    if (next) {
      setMessage(bar, messages.multiUnreached, {
        __AMOUNT__: formatMoney(next.amount - currentTotal, currency),
        __GOAL__: next.label,
      });
    } else {
      setMessage(bar, messages.allReached);
    }

    const tiers = bar.querySelectorAll('.free-shipping-bar__tier');
    let previousAmount = 0;

    tiers.forEach((tier, index) => {
      const threshold = thresholds[index];
      if (!threshold) return;

      const span = Math.max(1, threshold.amount - previousAmount);
      const progressAmount = Math.max(
        0,
        Math.min(span, currentTotal - previousAmount)
      );
      const complete = currentTotal >= threshold.amount;
      const active = !complete && progressAmount > 0;

      tier.style.flexBasis = `${(span / maxThreshold) * 100}%`;
      tier.classList.toggle('is-complete', complete);
      tier.classList.toggle('is-active', active);

      replaceProgress(
        tier.querySelector('.free-shipping-bar__tier-fill'),
        progressAmount,
        span
      );

      previousAmount = threshold.amount;
    });
  };

  const needsExactCartRead = (bar, rate, currentTotal) => {
    if (rate <= 1 || bar.dataset.distopionCurrencySynced === 'true') return false;

    const highestBaseThreshold = Math.max(
      ...baseThresholds.map((threshold) => threshold.amount)
    );

    return currentTotal >= highestBaseThreshold;
  };

  const syncBar = (bar, cart) => {
    const cartCurrency = String(
      cart?.currency ||
        window.Shopify?.currency?.active ||
        shopCurrency
    ).toUpperCase();
    const rate = currencyRate(cartCurrency);

    if (!rate) return false;

    let currentTotal = cartShippingSubtotal(cart);

    if (currentTotal === null) {
      const savedTotal = Number(bar.dataset.distopionCurrentTotal);

      currentTotal = Number.isFinite(savedTotal)
        ? savedTotal
        : domCurrentTotal(bar);

      if (needsExactCartRead(bar, rate, currentTotal)) return true;
    }

    const thresholds = convertedThresholds(rate);

    if (bar.classList.contains('free-shipping-bar--milestones')) {
      updateMilestones(bar, thresholds, currentTotal, cartCurrency);
    } else if (bar.classList.contains('free-shipping-bar--tiers')) {
      updateTiers(bar, thresholds, currentTotal, cartCurrency);
    } else {
      updateClassic(bar, thresholds, currentTotal, cartCurrency);
    }

    syncAmountLeftControl(bar, thresholds, currentTotal, cartCurrency);

    bar.dataset.distopionCurrencySynced = 'true';
    bar.dataset.distopionCurrentTotal = String(Math.round(currentTotal));
    bar.dataset.distopionCartCurrency = cartCurrency;

    return false;
  };

  const schedule = (cart) => {
    if (isExactCart(cart)) latestCart = cart;

    if (!frame) {
      frame = requestAnimationFrame(() => {
        frame = 0;
        syncAll(latestCart);
      });
    }

    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(() => syncAll(latestCart), 180);
  };

  const fetchCart = () => {
    if (cartRequest) return cartRequest;

    cartRequest = fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
        return response.json();
      })
      .then((cart) => {
        latestCart = cart;
        schedule(cart);
        return cart;
      })
      .catch((error) => {
        console.error('[Distopion currency thresholds]', error);
        return null;
      })
      .finally(() => {
        cartRequest = null;
      });

    return cartRequest;
  };

  const queueExactRead = (delay = 90) => {
    window.clearTimeout(exactReadTimer);
    exactReadTimer = window.setTimeout(fetchCart, delay);
  };

  const syncAll = (cart = latestCart) => {
    const bars = document.querySelectorAll('free-shipping-bar');
    let requiresCart = false;

    bars.forEach((bar) => {
      if (syncBar(bar, cart)) requiresCart = true;
    });

    if (requiresCart && !cart) {
      const read = () => fetchCart();

      if ('requestIdleCallback' in window) {
        requestIdleCallback(read, { timeout: 1000 });
      } else {
        window.setTimeout(read, 0);
      }
    }
  };

  const handleCartMutation = (event) => {
    const cart = event.detail?.cart;

    if (isExactCart(cart)) {
      schedule(cart);
    } else {
      // A number of theme cart events only carry section HTML or an item count.
      // Never reuse the previous cart after one of those events, because that
      // can leave tiers visually unlocked after a quantity decrease/removal.
      latestCart = null;
    }

    // This request is asynchronous and debounced, so it never delays the cart
    // interaction. It guarantees the bar is reconciled from Shopify's current
    // server cart after additions, removals, quantity changes and discounts.
    queueExactRead();
  };

  const start = () => {
    schedule();

    document.addEventListener('cart:change', handleCartMutation, { passive: true });
    document.addEventListener('cart:refresh', handleCartMutation, { passive: true });

    document.addEventListener(
      'shopify:section:load',
      () => {
        latestCart = null;
        schedule();
        queueExactRead(0);
      },
      { passive: true }
    );
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    latestCart = null;
    schedule();
    queueExactRead(0);
  });
})();
